/**
 * Transforms a paginated API response into grouped options for Ant Design Select OptGroup.
 * Groups items by a specified group key (e.g., group locations by zone).
 *
 * @param response - The API response with a `results` array
 * @param groupKey - The key on each item that holds the group object (e.g., "zone")
 * @param groupLabelKey - The key within the group object to use as the group label (default: "name")
 * @param labelKey - The key on each item to use as the option label (default: "name")
 * @param valueKey - The key on each item to use as the option value (default: "id")
 */
export const transformToGroupedOptions = (
    response: any,
    groupKey: string,
    groupLabelKey: string = "name",
    labelKey: string = "name",
    valueKey: string = "id",
) => {
    if (!response?.results) return [];

    const groups: Record<string, { label: string; value: string | number }[]> = {};
    const ungrouped: { label: string; value: string | number }[] = [];

    response.results.forEach((item: any) => {
        const group = item?.[groupKey];
        const option = {
            label: item?.[labelKey],
            value: item?.[valueKey],
        };

        if (group) {
            const groupLabel = group[groupLabelKey] || "Other";
            if (!groups[groupLabel]) {
                groups[groupLabel] = [];
            }
            groups[groupLabel].push(option);
        } else {
            ungrouped.push(option);
        }
    });

    const groupedOptions = Object.entries(groups).map(([label, options]) => ({
        label,
        options,
    }));

    // If there are ungrouped items, add them under "Other"
    if (ungrouped.length > 0) {
        groupedOptions.push({ label: "Other", options: ungrouped });
    }

    return groupedOptions;
};
